import { fbPixelId, gtmForwardedEvents } from "../constants/track";

const normalize = value =>
  value == null
    ? ""
    : String(value)
        .trim()
        .toLowerCase();

/**
 * Attaches the member's identity to the Pixel as Advanced Matching data.
 *
 * `fbq("init", pixelId, { em, fn, ln })` is the one call on which the Pixel library normalizes and SHA-256 hashes these
 * values before they leave the browser. The same values in `fbq("track", …)` are sent verbatim as custom event data,
 * as readable text in the request, which is what this module used to do. Never pass em/fn/ln to `fbq("track", …)`.
 *
 * The library keeps the first identity it is given for the lifetime of the page and ignores later ones, logging
 * "Duplicate Pixel ID" for each, so the identity is sent once per Pixel and every caller has to pass the member's real
 * values. See docs/testing/inventories/track.md.
 */
let identifiedFbq = null;

const attachIdentity = ({ em, fn, ln }) => {
  if (identifiedFbq === window.fbq) {
    return;
  }

  const identity = {};

  Object.entries({ em, fn, ln }).forEach(([key, value]) => {
    const normalized = normalize(value);

    if (normalized) {
      identity[key] = normalized;
    }
  });

  if (Object.keys(identity).length > 0) {
    window.fbq("init", fbPixelId, identity);
    identifiedFbq = window.fbq;
  }
};

export const track = (eventName, parameters) => {
  if (window.fbq) {
    const { em, fn, ln, ...eventData } = parameters || {};

    attachIdentity({ em, fn, ln });
    window.fbq("track", eventName, eventData);
  }
};

/**
 * Reports a dashboard event to Google Analytics 4 (GROWTH-8099). Name it in `eventNameDataLayer`; the rules for adding
 * one are in dashboard/docs/13-ga4-events.md.
 *
 * The `{ event, ...params }` push is what GTM reads. gtag.js ignores such pushes, so GA4 gets the event only from a GTM
 * tag (the four `gtmForwardedEvents`) or from the `gtag("event", …)` call below (every other event). Only the
 * production build defines `window.gtag` (helpers/analyticsTags.js); elsewhere the push stays in the page.
 */
export const trackDataLayer = (event, params) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event,
    ...params
  });

  if (typeof window.gtag === "function" && !gtmForwardedEvents.includes(event)) {
    window.gtag("event", event, { ...params });
  }
};
